import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";
import { useJobSummaries, useMaterialOfferScan, useTaskPolling } from "../hooks";
import type { JobSummaries, PriceSource, SummaryRow } from "../types";

function money(v?: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  return Number.isNaN(n) ? v : formatAmount(n);
}

function hostname(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

const SOURCE_TAG: Record<PriceSource, { label: string; cls: string; title: string }> = {
  catalog: {
    label: "cennik",
    cls: "bg-emerald-100 text-emerald-700",
    title: "Cena z cennika SEKOCENBUD / naszej bazy cen",
  },
  manual: {
    // Czerwień jak wszędzie indziej w wycenie: „to wpisał człowiek, nie cennik”.
    label: "ręczna",
    cls: "bg-red-100 text-red-700",
    title: "Cena wpisana ręcznie przez kosztorysanta",
  },
  ai: {
    label: "AI rynek",
    cls: "bg-violet-100 text-violet-700",
    title: "Cena rekomendowana przez AI (web search)",
  },
  investor: {
    label: "inwestorska",
    cls: "bg-gray-200 text-gray-700",
    title: "Dostawa inwestorska — wyceniona jawnie na 0 zł",
  },
  mixed: {
    label: "mieszana",
    cls: "bg-amber-100 text-amber-700",
    title: "Ten sam materiał wyceniono w kilku pozycjach różnie — cena ważona",
  },
  none: {
    label: "brak ceny",
    cls: "bg-red-100 text-red-700",
    title: "Brak ceny — pozycja liczona jako 0 zł",
  },
};

function SourceTag({ source }: { source: PriceSource }) {
  const t = SOURCE_TAG[source] ?? SOURCE_TAG.none;
  return (
    <span
      className={`px-1 py-0.5 text-[10px] font-medium whitespace-nowrap ${t.cls}`}
      title={t.title}
    >
      {t.label}
    </span>
  );
}

function RowDescription({ row }: { row: SummaryRow }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[var(--color-text-primary)]">
        {row.description || "—"}
        {row.investor_supplied && (
          <span className="ml-1 text-[10px] text-[var(--color-text-muted)]">
            (dostawa inwestorska)
          </span>
        )}
        {row.is_percent_aux && (
          <span className="ml-1 text-[10px] text-[var(--color-text-muted)]">
            (nakład procentowy)
          </span>
        )}
        {row.is_whole_position && (
          <span
            className="ml-1 text-[10px] text-[var(--color-text-muted)]"
            title="Pozycja bez nakładów wyceniona w całości, z narzutami „jak materiał” — dlatego jej kwota wchodzi do M."
          >
            (ryczałt)
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap text-[10px] text-[var(--color-text-muted)]">
        <SourceTag source={row.price_source} />
        {row.matched_code && <span>{row.matched_code}</span>}
        <span title="Pozycje kosztorysu, z których zsumowano ten wiersz">
          poz. {row.positions.slice(0, 8).join(", ")}
          {row.positions.length > 8 ? "…" : ""}
        </span>
      </div>
    </div>
  );
}

// ── Oferta znaleziona przez AI ───────────────────────────────────────────────

function OfferCell({ row }: { row: SummaryRow }) {
  const [open, setOpen] = useState(false);
  const offer = row.offer;
  if (!offer) return <span className="text-[var(--color-text-muted)]">—</span>;
  if (offer.status === "error")
    return (
      <span className="text-red-600 text-[11px]" title={offer.basis}>
        ⚠ błąd
      </span>
    );
  if (offer.status === "not_found" || offer.unit_price == null)
    return (
      <span className="text-[var(--color-text-muted)] text-[11px]" title={offer.basis}>
        nie znaleziono
      </span>
    );

  const saving = Number(offer.saving);
  return (
    <div className="space-y-0.5 text-right">
      <div className="tabular-nums text-[var(--color-text-primary)]">
        {money(offer.unit_price)} zł/{row.unit || "jm"}
      </div>
      <div
        className={`tabular-nums font-semibold ${
          saving > 0 ? "text-emerald-600" : "text-[var(--color-text-muted)]"
        }`}
      >
        {saving > 0 ? `−${money(offer.saving)} zł (${offer.saving_pct}%)` : "bez oszczędności"}
      </div>
      <button
        className="text-[10px] text-[var(--color-primary)] hover:underline"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? "zwiń" : "skąd?"}
      </button>
      {offer.stale && (
        <div
          className="text-[10px] text-amber-700"
          title="Kosztorys zmienił się po rozeznaniu — obmiar lub cena bazowa są inne niż w chwili skanu. Oszczędność przeliczono na aktualnych danych."
        >
          ⚠ nieaktualne
        </div>
      )}
      {open && (
        <div className="mt-1 p-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] text-left text-[10px] space-y-1">
          {offer.vendor && (
            <div className="font-medium text-[var(--color-text-primary)]">
              {offer.vendor}
              {offer.confidence && (
                <span className="ml-1 font-normal text-[var(--color-text-muted)]">
                  (pewność: {offer.confidence})
                </span>
              )}
            </div>
          )}
          {offer.basis && (
            <div className="text-[var(--color-text-secondary)]">{offer.basis}</div>
          )}
          {offer.delivery_note && (
            <div className="text-amber-700">⚠ {offer.delivery_note}</div>
          )}
          {offer.url && (
            <a
              className="block text-[var(--color-primary)] hover:underline break-all"
              href={offer.url}
              target="_blank"
              rel="noreferrer"
            >
              → oferta: {hostname(offer.url)}
            </a>
          )}
          {offer.sources.length > 0 && (
            <ul className="space-y-0.5">
              {offer.sources.map((s, i) => (
                <li key={i}>
                  <a
                    className="text-[var(--color-primary)] hover:underline break-all"
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {s.title || hostname(s.url)}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Zestawienie materiałów ───────────────────────────────────────────────────

function MaterialsTable({
  data,
  jobId,
  canEdit,
  threshold,
  onThreshold,
  onScanned,
}: {
  data: JobSummaries;
  jobId: string;
  canEdit: boolean;
  threshold: string;
  onThreshold: (v: string) => void;
  onScanned: () => void;
}) {
  const rows = data.materials.rows;
  const scannable = useMemo(
    () =>
      rows.filter(
        (r) =>
          !r.investor_supplied &&
          !r.is_percent_aux &&
          !r.is_whole_position &&
          Number(r.quantity) > 0,
      ),
    [rows],
  );
  const [selected, setSelected] = useState<Set<string>>(new Set(data.default_keys));
  const offers = useMaterialOfferScan(jobId);
  const [taskId, setTaskId] = useState<string | null>(null);
  const task = useTaskPolling(taskId);

  // Domyślne zaznaczenie idzie za progiem — dopóki użytkownik sam nie kliknie,
  // zmiana progu ma po prostu przestawić checkboxy.
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (!touched) setSelected(new Set(data.default_keys));
  }, [data.default_keys.join(","), touched]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const s = task.data?.status;
    if (s && ["success", "failure", "cancelled"].includes(s)) {
      if (s === "success") onScanned();
      setTaskId(null);
    }
  }, [task.data?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (key: string) => {
    setTouched(true);
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const allSelected = scannable.length > 0 && selected.size === scannable.length;
  const toggleAll = () => {
    setTouched(true);
    setSelected(allSelected ? new Set() : new Set(scannable.map((r) => r.key)));
  };

  const totals = data.materials.totals;
  const savings = data.savings;
  const drift = totals.value.avg !== totals.valuation_sum;

  return (
    <div className="card">
      <div className="px-4 py-3 bg-[var(--color-bg-elevated)] border-b border-[var(--color-border)] flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm font-semibold text-[var(--color-text-primary)]">
          Zestawienie materiałów
          <span className="ml-2 font-normal text-[var(--color-text-muted)]">
            {totals.count} poz. · liczone automatycznie z nakładów
          </span>
        </span>
        <span className="text-sm font-semibold text-[var(--color-primary)] tabular-nums whitespace-nowrap">
          {money(totals.value.avg)} zł
        </span>
      </div>

      {drift && (
        <div className="px-4 py-2 text-[11px] text-amber-700 bg-amber-50 border-b border-[var(--color-border)]">
          ⚠ Suma zestawienia ({money(totals.value.avg)} zł) różni się od pozycji M w
          wycenie ({money(totals.valuation_sum)} zł) — zgłoś to, bo nie powinno się
          zdarzyć.
        </div>
      )}

      {/* Pasek rozeznania rynku — z jawnym zastrzeżeniem, że oferta się nie zmienia. */}
      <div className="px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[11px] text-[var(--color-text-secondary)]">
            <strong>Rozeznanie rynku (AI).</strong> Szuka najtańszych ofert dla
            zaznaczonych materiałów i pokazuje możliwą oszczędność.{" "}
            <span className="text-[var(--color-text-primary)] font-medium">
              Nie zmienia wartości oferty przetargowej
            </span>{" "}
            — wyceny nie ruszamy, to tylko informacja zakupowa.
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-[var(--color-text-muted)] whitespace-nowrap">
                próg [zł]
                <input
                  className="ml-1 w-20 border rounded px-1.5 py-0.5 bg-[var(--color-bg-surface)] dark:border-gray-600 tabular-nums"
                  value={threshold}
                  onChange={(e) => {
                    setTouched(false);
                    onThreshold(e.target.value);
                  }}
                  title="Domyślnie zaznaczane są materiały, dla których ilość × cena przekracza ten próg"
                />
              </label>
              <Button
                size="sm"
                variant="secondary"
                disabled={selected.size === 0 || !!taskId}
                loading={!!taskId || offers.scan.isPending}
                onClick={async () => {
                  const r = await offers.scan.mutateAsync([...selected]);
                  setTaskId(r.task_id);
                }}
                title="AI przeszuka polski rynek (hurtownie, cenniki) i poda najtańszą ofertę wraz ze źródłami"
              >
                {taskId ? "Szukam ofert…" : `Szukaj tańszych ofert (${selected.size})`}
              </Button>
              {savings.scanned_rows > 0 && (
                <button
                  className="text-[11px] text-[var(--color-text-muted)] hover:underline whitespace-nowrap"
                  onClick={() => offers.clear.mutate()}
                >
                  wyczyść wyniki
                </button>
              )}
            </div>
          )}
        </div>

        {savings.scanned_rows > 0 && (
          <div className="p-2 border border-emerald-300 bg-emerald-50 text-[11px] text-emerald-900 space-y-0.5">
            <div>
              <strong>
                Potencjalna oszczędność: {money(savings.total)} zł
              </strong>{" "}
              ({savings.pct_of_materials}% wartości materiałów) — na{" "}
              {savings.rows_with_saving} z {savings.scanned_rows} sprawdzonych pozycji
              (sprawdzono materiały za {money(savings.checked_value)} zł).
            </div>
            <div className="text-emerald-800">
              Wartość oferty przetargowej pozostaje bez zmian — oszczędność zrealizuje
              się dopiero na zakupie. Ceny netto, bez dostawy o ile nie zapisano inaczej
              przy ofercie; przed decyzją zweryfikuj źródło.
            </div>
          </div>
        )}
        {taskId && (
          <div className="text-[11px] text-[var(--color-text-muted)] whitespace-pre-wrap">
            {task.data?.log?.split("\n").slice(-2).join("\n") || "Uruchamiam…"}
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[var(--color-text-muted)] bg-[var(--color-bg-surface)]">
              <th className="px-2 py-2 w-8">
                {canEdit && (
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    title="Zaznacz / odznacz wszystkie"
                  />
                )}
              </th>
              <th className="px-2 py-2 text-left font-medium">Materiał</th>
              <th className="px-2 py-2 text-left font-medium">Jm</th>
              <th className="px-2 py-2 text-right font-medium">Ilość</th>
              <th className="px-2 py-2 text-right font-medium">Cena jedn.</th>
              <th className="px-2 py-2 text-right font-medium">Wartość (śr.)</th>
              <th className="px-2 py-2 text-right font-medium">
                Najtańsza oferta (AI)
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const selectable =
                !row.investor_supplied &&
                !row.is_percent_aux &&
                !row.is_whole_position &&
                Number(row.quantity) > 0;
              return (
                <tr
                  key={row.key}
                  className="border-t border-[var(--color-border)] align-top"
                >
                  <td className="px-2 py-1.5 text-center">
                    {canEdit && selectable && (
                      <input
                        type="checkbox"
                        checked={selected.has(row.key)}
                        onChange={() => toggle(row.key)}
                      />
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <RowDescription row={row} />
                  </td>
                  <td className="px-2 py-1.5 text-[var(--color-text-secondary)]">
                    {row.unit || "—"}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {row.is_percent_aux ? "—" : row.quantity}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {money(row.unit_price)}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                    {money(row.value.avg)}
                  </td>
                  <td className="px-2 py-1.5">
                    <OfferCell row={row} />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[var(--color-border)] bg-[var(--color-bg-surface)] font-semibold">
              <td />
              <td className="px-2 py-2">Razem materiały</td>
              <td />
              <td />
              <td />
              <td className="px-2 py-2 text-right tabular-nums">
                {money(totals.value.avg)}
              </td>
              <td className="px-2 py-2 text-right tabular-nums text-emerald-700">
                {Number(savings.total) > 0 ? `−${money(savings.total)}` : ""}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

// ── Zestawienie sprzętu ──────────────────────────────────────────────────────

function EquipmentTable({ data }: { data: JobSummaries }) {
  const rows = data.equipment.rows;
  const totals = data.equipment.totals;
  const drift = totals.value.avg !== totals.valuation_sum;

  return (
    <div className="card">
      <div className="px-4 py-3 bg-[var(--color-bg-elevated)] border-b border-[var(--color-border)] flex items-center justify-between gap-3 flex-wrap">
        <span className="text-sm font-semibold text-[var(--color-text-primary)]">
          Zestawienie sprzętu
          <span className="ml-2 font-normal text-[var(--color-text-muted)]">
            {totals.count} poz. · razem {totals.motohours} m-g
          </span>
        </span>
        <span className="text-sm font-semibold text-[var(--color-primary)] tabular-nums whitespace-nowrap">
          {money(totals.value.avg)} zł
        </span>
      </div>
      {drift && (
        <div className="px-4 py-2 text-[11px] text-amber-700 bg-amber-50 border-b border-[var(--color-border)]">
          ⚠ Suma zestawienia różni się od pozycji S w wycenie (
          {money(totals.valuation_sum)} zł).
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[var(--color-text-muted)] bg-[var(--color-bg-surface)]">
              <th className="px-2 py-2 text-left font-medium">Sprzęt</th>
              <th className="px-2 py-2 text-left font-medium">Jm</th>
              <th className="px-2 py-2 text-right font-medium">Motogodziny</th>
              <th className="px-2 py-2 text-right font-medium">Stawka</th>
              <th className="px-2 py-2 text-right font-medium">Wartość (śr.)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key} className="border-t border-[var(--color-border)] align-top">
                <td className="px-2 py-1.5">
                  <RowDescription row={row} />
                </td>
                <td className="px-2 py-1.5 text-[var(--color-text-secondary)]">
                  {row.unit || "—"}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {row.quantity}
                  {!row.is_motohours && (
                    <span
                      className="ml-1 text-[10px] text-[var(--color-text-muted)]"
                      title="Nakład rozliczany w innej jednostce niż motogodzina"
                    >
                      ({row.unit || "?"})
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums">
                  {money(row.unit_price)}
                </td>
                <td className="px-2 py-1.5 text-right tabular-nums font-medium">
                  {money(row.value.avg)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-[var(--color-border)] bg-[var(--color-bg-surface)] font-semibold">
              <td className="px-2 py-2">Razem sprzęt</td>
              <td />
              <td className="px-2 py-2 text-right tabular-nums">{totals.motohours}</td>
              <td />
              <td className="px-2 py-2 text-right tabular-nums">
                {money(totals.value.avg)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/** Zestawienie materiałów + sprzętu. Obie tabele są WYLICZANE z nakładów
 *  wycenionych pozycji, więc istnieją tylko dla metody szczegółowej. */
export function SummaryTables({
  jobId,
  canEdit,
  hasDetailed,
}: {
  jobId: string;
  canEdit: boolean;
  hasDetailed: boolean;
}) {
  const [threshold, setThreshold] = useState("1000");
  // Próg wchodzi do klucza zapytania — nie odpytujemy backendu przy każdym
  // wciśniętym znaku.
  const [debounced, setDebounced] = useState(threshold);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(threshold || "0"), 400);
    return () => clearTimeout(t);
  }, [threshold]);

  const { data, isLoading, refetch } = useJobSummaries(jobId, debounced, hasDetailed);

  if (!hasDetailed)
    return (
      <div className="card p-4 text-xs text-[var(--color-text-secondary)]">
        <strong>Zestawienie materiałów i sprzętu</strong> powstaje z nakładów R/M/S —
        metoda uproszczona ich nie ma (cena jednostkowa BCJ zawiera wszystko w jednej
        kwocie). Kliknij „Rozwiń do nakładczego”, żeby je policzyć.
      </div>
    );
  if (isLoading || !data)
    return (
      <div className="card p-4 text-xs text-[var(--color-text-secondary)]">
        Liczę zestawienia…
      </div>
    );
  if (data.materials.rows.length === 0 && data.equipment.rows.length === 0)
    return (
      <div className="card p-4 text-xs text-[var(--color-text-secondary)]">
        Brak nakładów materiałowych i sprzętowych do zestawienia.
      </div>
    );

  return (
    <div className="space-y-4">
      {data.materials.rows.length > 0 && (
        <MaterialsTable
          data={data}
          jobId={jobId}
          canEdit={canEdit}
          threshold={threshold}
          onThreshold={setThreshold}
          onScanned={() => refetch()}
        />
      )}
      {data.equipment.rows.length > 0 && <EquipmentTable data={data} />}
    </div>
  );
}
